import { useContext, useCallback, useEffect, useRef } from "react";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { AnalyticsContext, CartOverlaysContext } from "@swiggy-private/checkout-core";

//types
import { ICroutonList, CroutonIds, ShowCroutonArgs } from "@swiggy-private/cart-types";
import { IGenericCrouton, SUPPORTED_CROUTON_TYPES } from "./types";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// styles
import styles from "./useGenericCrouton.scss";

//analytics
import { timedFreeCashCroutonImpressionEvent } from "@cart-client/analytics/events/TimedFreeCashCrouton";
import { magicItemsErrorCroutonImpressionEvent } from "@cart-client/analytics/events/CrossSellM2";
import { hotelInRoomDeliveryCroutonImpressionEvent } from "@cart-client/analytics/events/genericCrouton";

//utils
import { isEmpty } from "@swiggy-private/js-utils";

// hooks
import { useDealRushCrouton } from "@cart-client/hooks/useDealRushCrouton";
import { useDeliverySpeedCrouton } from "@cart-client/hooks/useDeliverySpeedCrouton";

const useGenericCrouton = (props: IGenericCrouton) => {
    const { setCroutonVisibility, canShowTimer, uploadId } = props;
    const { cartState } = useContext(CartContext);
    const { registerCrouton, onCloseCroutonImmediate } = useContext(CartOverlaysContext);
    const { reloadCart } = useContext(UpdateCartContext);
    const { impressionEvent } = useContext(AnalyticsContext);

    const { reviewedCart: cartResponse } = cartState;
    const croutonList = cartResponse?.data?.croutonList;
    const prevCroutonListRef = useRef<typeof croutonList>();

    // Use refs to avoid unnecessary re-registrations when context values change
    const impressionEventRef = useRef(impressionEvent);
    impressionEventRef.current = impressionEvent;

    const onCloseCroutonImmediateRef = useRef(onCloseCroutonImmediate);
    onCloseCroutonImmediateRef.current = onCloseCroutonImmediate;

    const reloadCartRef = useRef(reloadCart);
    reloadCartRef.current = reloadCart;

    const dealRushCroutonData = croutonList?.find((crouton) => crouton.type === CroutonIds.DEAL_RUSH)?.data;
    const payloadForCroutonRegister = useDealRushCrouton(dealRushCroutonData);

    const { getDeliverySpeedCroutonRegistration, isDeliverySectionMounted } = useDeliverySpeedCrouton({
        setCroutonVisibility,
        onCloseCroutonImmediate,
    });

    const onCompleteTimer = useCallback(() => {
        reloadCartRef.current();
    }, []);

    const timerKey = cartState.uniqueIdForCart;

    // Generic impression event handler for all crouton types
    const onCroutonImpressionEvent = useCallback(
        (type: CroutonIds, data: ICroutonList["data"]) => {
            switch (type) {
                case CroutonIds.TIMED_FREE_CASH_CROUTON:
                    timedFreeCashCroutonImpressionEvent(data?.text || "", impressionEventRef.current, data?.timer, {
                        uploadId,
                        restaurantId: String(cartState.localCart?.restaurantId ?? ""),
                        cartId: String(cartResponse?.data?.cartId ?? ""),
                    });
                    break;
                case CroutonIds.MAGIC_ITEMS:
                    magicItemsErrorCroutonImpressionEvent(
                        impressionEventRef.current,
                        String(cartResponse?.data?.cartId || ""),
                        data?.text || "",
                    );
                    break;
                case CroutonIds.HOTEL_IN_ROOM_DELIVERY:
                    hotelInRoomDeliveryCroutonImpressionEvent(
                        impressionEventRef.current,
                        String(cartResponse?.data?.cartId || ""),
                        data?.text || "",
                    );
                    break;
                default:
                    break;
            }
        },
        [cartResponse?.data?.cartId, uploadId, cartState.localCart?.restaurantId],
    );

    const croutonContent = useCallback(
        (crouton: ICroutonList, index: number) => {
            const { type, data } = crouton;
            switch (type) {
                case CroutonIds.TIMED_FREE_CASH_CROUTON:
                    return {
                        id: type,
                        priority: index + 1,
                        content: {
                            title: data?.text,
                            icon: data?.icon || "",
                            showTimer: data?.showTimer,
                            duration: data?.timer,
                            durationOfCrouton: data?.timeout,
                            onCompleteTimer,
                            setCroutonVisibility,
                            timerTextColor: data?.timerTextColor,
                            textColor: data?.textColor,
                            wrapperClassName: styles.genericCrouton,
                            iconClassName: styles.genericCroutonIcon,
                            titleClassName: styles.genericCroutonText,
                            buttonClassName: styles.genericCroutonBtn,
                            timerClassName: styles.genericCroutonTimer,
                            cssProperties: data?.backgroundColor ? { background: data.backgroundColor } : undefined,
                            impressionEvent: () => {
                                onCroutonImpressionEvent(type, data);
                            },
                            timerKey,
                        },
                        forceRefreshContent: true,
                    };
                case CroutonIds.DEAL_RUSH:
                    if (isEmpty(payloadForCroutonRegister?.title)) {
                        return null;
                    }
                    return {
                        id: type,
                        priority: index + 1,
                        content: {
                            ...payloadForCroutonRegister,
                            setCroutonVisibility,
                            wrapperClassName: styles.genericCrouton,
                            iconClassName: styles.genericCroutonIcon,
                            buttonClassName: styles.genericCroutonBtn,
                            timerClassName: styles.genericCroutonTimer,
                        },
                        forceRefreshContent: true,
                    };
                case CroutonIds.MAGIC_ITEMS:
                    return {
                        id: type,
                        priority: index + 1,
                        content: {
                            title: data?.text,
                            icon: data?.icon || "",
                            showTimer: data?.showTimer,
                            duration: data?.timer,
                            durationOfCrouton: data?.timeout,
                            setCroutonVisibility,
                            timerTextColor: data?.timerTextColor,
                            textColor: data?.textColor,
                            titleClassName: styles.genericCroutonText,
                            onCloseCrouton: () => onCloseCroutonImmediateRef.current(),
                            impressionEvent: () => {
                                onCroutonImpressionEvent(type, data);
                            },
                        },
                        forceRefreshContent: true,
                    };
                case CroutonIds.DELIVERY_SPEED_TOING:
                    return getDeliverySpeedCroutonRegistration(crouton, index);
                default:
                    if (!SUPPORTED_CROUTON_TYPES.includes(type)) {
                        return null;
                    }
                    return {
                        id: type,
                        priority: index + 1,
                        content: {
                            title: data?.text,
                            subText: data?.subText,
                            icon: data?.icon || "",
                            showTimer: data?.showTimer,
                            duration: data?.timer,
                            durationOfCrouton: data?.timeout,
                            setCroutonVisibility,
                            timerTextColor: data?.timerTextColor,
                            textColor: data?.textColor,
                            backgroundColor: data?.backgroundColor,
                            borderRadius: data?.borderRadius,
                            titleClassName: styles.genericCroutonText,
                            iconClassName: styles.genericCroutonIcon,
                            onCloseCrouton: () => onCloseCroutonImmediateRef.current(),
                            impressionEvent: () => {
                                onCroutonImpressionEvent(type, data);
                            },
                        },
                        forceRefreshContent: true,
                    };
            }
        },
        [
            onCompleteTimer,
            setCroutonVisibility,
            timerKey,
            payloadForCroutonRegister,
            onCroutonImpressionEvent,
            getDeliverySpeedCroutonRegistration,
        ],
    );

    const isSupportedCroutonType = useCallback(() => {
        if (isEmpty(croutonList)) {
            return false;
        }
        return croutonList?.some((crouton) => SUPPORTED_CROUTON_TYPES.includes(crouton.type));
    }, [croutonList]);

    const registerGenericCrouton = useCallback(() => {
        if (isEmpty(croutonList)) {
            return;
        }
        croutonList?.forEach((crouton, index) => {
            const content = croutonContent(crouton, index);
            if (!isEmpty(content)) {
                registerCrouton(content as ShowCroutonArgs);
            }
        });
    }, [croutonContent, croutonList, registerCrouton]);

    // Use ref to avoid useEffect running when registerGenericCrouton changes
    const registerGenericCroutonRef = useRef(registerGenericCrouton);
    registerGenericCroutonRef.current = registerGenericCrouton;

    // Track Deal Rush state changes
    const hasDealRushPayload = !isEmpty(payloadForCroutonRegister?.title);
    const dealRushShowTimer = payloadForCroutonRegister?.showTimer;

    useEffect(() => {
        if (canShowTimer) {
            return;
        }

        const hadCroutons = !isEmpty(prevCroutonListRef.current);
        const hasCroutons = !isEmpty(croutonList);
        const hasSupportedCroutonType = isSupportedCroutonType();

        if (hadCroutons && (!hasCroutons || !hasSupportedCroutonType)) {
            onCloseCroutonImmediateRef.current();
        } else if (hasSupportedCroutonType) {
            registerGenericCroutonRef.current();
        }

        prevCroutonListRef.current = croutonList;
    }, [
        croutonList,
        isSupportedCroutonType,
        canShowTimer,
        hasDealRushPayload,
        dealRushShowTimer,
        isDeliverySectionMounted,
    ]);
};

export default useGenericCrouton;
